import React from 'react';

import { NotificationPanelComponent } from 'design/notificationPanel';
import { TextComponent } from 'design/text/textComponents';
import { useServices } from 'hooks/useServices';
import { useAsyncController } from 'hooks/useAsyncController';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MINUTE_MS } from 'utils/constants';
import { setCustomInterval } from 'utils/setCustomInterval';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';
import { useSafeAsyncWrapper } from 'hooks/useSafeAsyncWrapper';
import { DialogComponent } from 'design/dialog';
import { writeToClipboard } from 'utils/clipboardUtils';
import { GridContainerComponent } from 'design/grid';
import { BankName } from 'types/bankName';
import { truncateString } from 'utils/stringUtils';

type Props = {
  bankName: BankName;
};

export const BankingSessionStatusPanel: React.FC<Props> = (props) => {
  const { bankName } = props;
  const {
    bankingSessionIsActive,
    getBankingSessionRenewalLink,
  } = useServices();

  const wrapAsyncSafely = useSafeAsyncWrapper();

  const { asyncProcess, wrapAsync } = useAsyncController<boolean>();

  const { bankingSessionStatusPanel: intl } = useIntlDictionary();

  const [renewalLink, setRenewalLink] = React.useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const checkStatus = React.useCallback(() => {
    wrapAsync(bankingSessionIsActive(bankName).catch((error) => error));
  }, [wrapAsync, bankingSessionIsActive, bankName]);

  // Проверка статуса сессии при монтировании
  React.useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Запуск интервала для проверки статуса сессии
  React.useEffect(() => {
    return setCustomInterval(() => {
      checkStatus();
    }, MINUTE_MS * 2);
  }, [checkStatus]);

  const isActive = asyncProcess.status === 'RESOLVED' && asyncProcess.value;

  const isPending = asyncProcess.status === 'PENDING';

  const errorText =
    asyncProcess.status === 'REJECTED' && bankName === 'PRIOR'
      ? truncateString(asyncProcess.error.message)
      : null;

  const statusText = isPending
    ? intl.statusPendingText
    : isActive
    ? intl.statusActiveText(bankName)
    : errorText ?? intl.statusInactiveText(bankName);

  const getRenewalLink = React.useCallback(() => {
    wrapAsyncSafely(
      getBankingSessionRenewalLink().then((link) => {
        setRenewalLink(link);
        setIsDialogOpen(true);
      })
    );
  }, [getBankingSessionRenewalLink, wrapAsyncSafely]);

  const handleCloseDialog = React.useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleCopyLink = React.useCallback(() => {
    if (renewalLink) {
      wrapAsyncSafely(
        writeToClipboard(renewalLink).then(() => {
          setIsDialogOpen(false);
        })
      );
    }
  }, [renewalLink, wrapAsyncSafely]);

  return (
    <>
      <NotificationPanelComponent
        intent={isPending ? 'warning' : isActive ? 'neutral' : 'critical'}
        text={<TextComponent size="s">{statusText}</TextComponent>}
        primaryAction={
          !isPending && (
            <ClickControlComponent onClick={checkStatus}>
              <TextComponent size="s" weight="bold" color="accent" upperCase>
                {intl.refreshButton}
              </TextComponent>
            </ClickControlComponent>
          )
        }
        secondaryAction={
          !isPending &&
          bankName === 'PRIOR' && (
            <ClickControlComponent onClick={getRenewalLink}>
              <TextComponent size="s" weight="bold" color="accent" upperCase>
                {intl.generateTokensButton}
              </TextComponent>
            </ClickControlComponent>
          )
        }
      />
      <DialogComponent
        open={isDialogOpen}
        title={
          <TextComponent size="m" weight="medium">
            {intl.dialogTitle}
          </TextComponent>
        }
        details={<TextComponent size="s">{renewalLink}</TextComponent>}
        actions={
          <GridContainerComponent gap={15}>
            <ClickControlComponent onClick={handleCopyLink}>
              <TextComponent size="s" weight="bold" color="accent">
                {intl.dialogCopyButton}
              </TextComponent>
            </ClickControlComponent>
            <ClickControlComponent onClick={handleCloseDialog}>
              <TextComponent size="s" weight="bold" color="secondary">
                {intl.dialogCloseButton}
              </TextComponent>
            </ClickControlComponent>
          </GridContainerComponent>
        }
      />
    </>
  );
};
